import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip } from "recharts";
import { Link } from "react-router-dom";
import { ArrowUpRight, Storefront, UsersThree, Info } from "@phosphor-icons/react";
import StatusPill from "@/components/StatusPill";
import LocationBadge from "@/components/LocationBadge";

export default function MiNegocio() {
  const [overview, setOverview] = useState(null);
  const [playbooks, setPlaybooks] = useState([]);
  const [settings, setSettings] = useState(null);
  const [savingKey, setSavingKey] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/overview"), api.get("/playbooks"), api.get("/settings")])
      .then(([ov, pb, st]) => {
        setOverview(ov.data);
        setPlaybooks(pb.data.playbooks || []);
        setSettings(st.data);
      })
      .catch(() => toast.error("No se pudo cargar la información del negocio"))
      .finally(() => setLoading(false));
  }, []);

  const saveStatus = async (key, value) => {
    setSavingKey(key);
    try {
      const r = await api.put("/settings", { [key]: value });
      setSettings(r.data);
      // Refresh overview so the compliance summary re-computes.
      const ov = await api.get("/overview");
      setOverview(ov.data);
      toast.success("Estado actualizado");
    } catch {
      toast.error("No se pudo actualizar el estado");
    } finally {
      setSavingKey(null);
    }
  };

  const stageData = (overview?.active_playbook?.stages || []).map((s) => {
    const done = s.milestones.filter((m) => m.completed).length;
    const total = s.milestones.length || 1;
    return { name: s.name, avance: Math.round((done / total) * 100) };
  });

  return (
    <div data-testid="page-mi-negocio">
      <header className="mb-8">
        <div className="label-mono text-muted-foreground flex items-center gap-2 flex-wrap">
          <span>Mi Negocio</span>
          <span aria-hidden>·</span>
          <LocationBadge variant="tag" testid="mi-negocio-location-badge" />
        </div>
        <h1 className="display text-4xl sm:text-5xl md:text-6xl mt-1 leading-[0.95]">Vista general</h1>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-8">
        <KPI label="Progreso" value={`${overview?.progress_pct ?? 0}%`} />
        <KPI label="Rutas" value={overview?.counts?.playbooks ?? 0} />
        <KPI label="Tareas activas" value={overview?.counts?.tasks_pending ?? 0} />
        <KPI label="Clientes" value={overview?.counts?.clients ?? 0} />
      </div>

      <section className="mb-10 brut-card" data-testid="estado-del-negocio">
        <div className="label-mono text-muted-foreground">Estado del negocio</div>
        <h2 className="display text-2xl mt-1">¿Cómo describirías tu negocio ahora?</h2>
        <p className="text-xs text-muted-foreground mt-2 max-w-md leading-relaxed flex items-start gap-1.5">
          <Info size={12} weight="bold" className="flex-shrink-0 mt-0.5" />
          Ruta actualiza tu estado automáticamente conforme avanzas. Si algo no coincide, ajústalo aquí — los requisitos locales se re-calculan al instante.
        </p>
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <StatusToggle
            testid="toggle-is-selling"
            Icon={Storefront}
            title="Ya vendo"
            hint="Marca esto si ya generas ingresos, aunque sean pequeños."
            value={!!settings?.is_selling}
            saving={savingKey === "is_selling"}
            onChange={(v) => saveStatus("is_selling", v)}
          />
          <StatusToggle
            testid="toggle-is-hiring"
            Icon={UsersThree}
            title="Ya contrato"
            hint="Marca esto si tienes al menos una persona trabajando contigo."
            value={!!settings?.is_hiring}
            saving={savingKey === "is_hiring"}
            onChange={(v) => saveStatus("is_hiring", v)}
          />
        </div>
      </section>

      <section className="mb-10 brut-card" data-testid="grafica-avance">
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="label-mono text-muted-foreground">Ruta activa</div>
            <h2 className="display text-2xl mt-1">
              {overview?.active_playbook?.name || "Sin ruta activa"}
            </h2>
          </div>
          {overview?.active_playbook && (
            <Link to={`/playbooks/${overview.active_playbook.id}`} className="btn-secondary flex items-center gap-2">
              Abrir <ArrowUpRight size={14} weight="bold" />
            </Link>
          )}
        </div>
        {stageData.length > 0 ? (
          <div className="w-full h-64 mt-4">
            <ResponsiveContainer>
              <BarChart data={stageData} margin={{ top: 10, right: 10, left: -20, bottom: 10 }}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="0" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono", fill: "hsl(var(--foreground))" }} interval={0} angle={-20} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 10, fill: "hsl(var(--foreground))" }} tickFormatter={(v) => `${v}%`} />
                <Tooltip
                  contentStyle={{ background: "hsl(var(--surface))", border: "2px solid hsl(var(--foreground))", borderRadius: 0, fontFamily: "IBM Plex Mono" }}
                  formatter={(v) => [`${v}%`, "Avance"]}
                />
                <Bar dataKey="avance" fill="hsl(var(--primary))" stroke="hsl(var(--foreground))" strokeWidth={2} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="text-sm text-muted-foreground">Aún no hay un playbook con etapas para graficar.</div>
        )}
      </section>

      <section>
        <div className="label-mono text-muted-foreground mb-3">Proyectos activos</div>
        {loading && <div className="text-sm text-muted-foreground">Cargando…</div>}
        <div className="grid md:grid-cols-2 gap-4">
          {playbooks.map((p) => {
            const total = p.stages.reduce((acc, s) => acc + s.milestones.length, 0);
            const done = p.stages.reduce((acc, s) => acc + s.milestones.filter((m) => m.completed).length, 0);
            const pct = total ? Math.round((done / total) * 100) : 0;
            return (
              <Link key={p.id} to={`/playbooks/${p.id}`} className="brut-card hover:bg-muted transition-colors" data-testid={`proyecto-${p.id}`}>
                <div className="flex items-center justify-between">
                  <div className="display text-xl">{p.name}</div>
                  <ArrowUpRight size={18} weight="bold" />
                </div>
                <div className="text-xs text-muted-foreground mt-1">{p.category} · {p.stages.length} etapas</div>
                <div className="mt-3 h-2 border-2 border-foreground">
                  <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
                </div>
                <div className="mt-2 flex items-center justify-between text-xs">
                  <span className="label-mono text-muted-foreground">{done}/{total} hitos</span>
                  <StatusPill value={pct === 100 ? "completada" : (pct > 0 ? "en_progreso" : "pendiente")} />
                </div>
              </Link>
            );
          })}
          {!loading && playbooks.length === 0 && (
            <div className="text-sm text-muted-foreground">Todavía no tienes proyectos. Elige uno en <Link to="/explorar" className="underline font-semibold">Explorar Rutas</Link>.</div>
          )}
        </div>
      </section>
    </div>
  );
}

function KPI({ label, value }) {
  return (
    <div className="brut-card !p-3 sm:!p-5" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="label-mono text-muted-foreground text-[0.65rem] sm:text-xs truncate">{label}</div>
      <div className="display text-3xl sm:text-4xl mt-1 leading-none">{value}</div>
    </div>
  );
}

function StatusToggle({ testid, Icon, title, hint, value, saving, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      disabled={saving}
      data-testid={testid}
      data-active={value ? "true" : "false"}
      aria-pressed={value}
      className={`text-left border-2 border-foreground p-3 flex items-start gap-3 transition-colors ${
        value
          ? "bg-primary text-primary-foreground shadow-[3px_3px_0_0_hsl(var(--foreground))]"
          : "bg-surface hover:bg-muted"
      } ${saving ? "opacity-60" : ""}`}
    >
      <div className={`w-9 h-9 border-2 border-foreground flex items-center justify-center flex-shrink-0 ${value ? "bg-background text-foreground" : "bg-primary text-primary-foreground"}`}>
        <Icon size={18} weight="bold" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-sm">{title}</span>
          <span className={`label-mono text-[0.6rem] px-1.5 py-0.5 border-2 border-foreground ${value ? "bg-background text-foreground" : "bg-muted text-muted-foreground"}`}>
            {value ? "SÍ" : "NO"}
          </span>
        </div>
        <p className={`text-xs mt-1 leading-relaxed ${value ? "opacity-90" : "text-muted-foreground"}`}>{hint}</p>
      </div>
    </button>
  );
}
